// oidc-client keeps everything under the `oidc.` prefix: the signed-in user
// in sessionStorage (`oidc.user:<authority>:<client_id>`) and every in-flight
// login transaction in localStorage (`oidc.<state>`).
const OIDC_KEY_PREFIX = 'oidc.';
const OIDC_USER_KEY_PREFIX = 'oidc.user:';

// Same window oidc-client uses in State.clearStaleState (DefaultStaleStateAge).
const STALE_STATE_AGE_SECONDS = 60 * 15;

// `oauth_state` is legacy: nothing writes it any more, but entries left by
// earlier builds are still worth clearing. `callback_redirect_uri` is
// deliberately absent - it holds the page the user was heading to before being
// sent to login, and the callback needs it to get them back there.
const LEGACY_AUTH_KEYS = new Set(['oauth_state', 'oauth_error']);

// localStorage is shared by every tab, so another tab's login may be in flight
// right now. Only drop transactions old enough that oidc-client would call them
// stale; otherwise clicking Login here breaks a login in the tab next door.
const isStaleTransaction = (value: string | null) => {
  if (!value) {
    return true;
  }

  try {
    const { created } = JSON.parse(value);

    // Shape we do not recognise: keep it. Failing toward "keep" costs nothing,
    // since clear-then-create protects this click's transaction either way, and
    // it stops a future oidc-client change reopening the cross-tab case.
    if (typeof created !== 'number') {
      return false;
    }

    return created <= Date.now() / 1000 - STALE_STATE_AGE_SECONDS;
  } catch (e) {
    // Corrupt entries are not live transactions.
    return true;
  }
};

const clearStore = (store: Storage) => {
  Object.keys(store).forEach((key) => {
    if (LEGACY_AUTH_KEYS.has(key) || key.startsWith(OIDC_USER_KEY_PREFIX)) {
      store.removeItem(key);
      return;
    }

    if (
      key.startsWith(OIDC_KEY_PREFIX) &&
      isStaleTransaction(store.getItem(key))
    ) {
      store.removeItem(key);
    }
  });
};

export const clearAuthDataBeforeLogin = () => {
  clearStore(localStorage);
  clearStore(sessionStorage);

  const authCookiePatterns = ['oidc', 'auth', 'session', 'token', 'state'];

  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    let name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    name = name.trim().toLowerCase();

    if (authCookiePatterns.some((pattern) => name.includes(pattern))) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }
  }
};
